/* eslint-disable no-param-reassign */

import { createAsyncThunk, createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { questionnaireApi } from '../api';
import { ApplicationQuestionnaireDetail } from '../api/openapi';
import i18n from '../i18n';
import { RootState } from './rootReducer';
import { AppThunk } from './store';

/*
 * State
 */
interface State {
  applicationQuestionnaire: ApplicationQuestionnaireDetail | null;
  applicationQuestionnaireLoading: 'idle' | 'pending';
  applicationQuestionnaireError: null | SerializedError;

  questionnaireAnswers: Record<string, number>;
  questionnaireAnswersSaving: boolean;
}

/*
 * fetchApplicationQuestionnaire
 */
export const fetchApplicationQuestionnaire = createAsyncThunk<
  ApplicationQuestionnaireDetail,
  string,
  {
    state: RootState;
  }
>('questionnaire/fetchApplicationQuestionnaire', async (id) => {
  const response = await questionnaireApi.applicationQuestionnaireRetrieve({
    uuid: id,
  });
  return response.data;
});

const initialState: State = {
  applicationQuestionnaire: null,
  applicationQuestionnaireLoading: 'idle',
  applicationQuestionnaireError: null,

  questionnaireAnswers: {},
  questionnaireAnswersSaving: false,
};

/*
 * slice
 */
const slice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    questionnaireAnswerUpdated(
      state,
      action: PayloadAction<{
        id: string;
        answer: number;
      }>
    ): void {
      state.questionnaireAnswers[action.payload.id] = action.payload.answer;
    },
    saveQuestionnaireAnswersStarted(state) {
      state.questionnaireAnswersSaving = true;
    },
    saveQuestionnaireAnswersCompleted(state) {
      state.questionnaireAnswersSaving = false;
    },
  },
  extraReducers: (builder) => {
    /*
     * fetchApplicationQuestionnaire
     */
    builder.addCase(fetchApplicationQuestionnaire.pending, (state) => {
      state.applicationQuestionnaire = null;
      state.applicationQuestionnaireError = null;
      state.applicationQuestionnaireLoading = 'pending';
      state.questionnaireAnswers = {};
    });
    builder.addCase(fetchApplicationQuestionnaire.fulfilled, (state, { payload }) => {
      state.applicationQuestionnaire = payload;
      state.applicationQuestionnaireLoading = 'idle';
      state.questionnaireAnswers = payload.answers.reduce((acc, val) => ({ ...acc, [val.id]: val.answer }), {});
    });
    builder.addCase(fetchApplicationQuestionnaire.rejected, (state, action) => {
      state.applicationQuestionnaireError = action.error;
      state.applicationQuestionnaireLoading = 'idle';
    });
  },
});

export const { questionnaireAnswerUpdated } = slice.actions;

export const saveQuestionnaireAnswers = (): AppThunk => async (dispatch, getState) => {
  const questinonaireId = getState().questionnaire.applicationQuestionnaire?.id;
  const questinonaireAnswers = getState().questionnaire.questionnaireAnswers;

  if (questinonaireId) {
    dispatch(slice.actions.saveQuestionnaireAnswersStarted());

    try {
      await questionnaireApi.applicationQuestionnaireAnswer({
        uuid: questinonaireId,
        applicationQuestionnaireAnswerInputRequest: {
          answers: Object.entries(questinonaireAnswers).map(([k, v]) => ({
            id: k,
            answer: v,
          })),
        },
      });

      await dispatch(fetchApplicationQuestionnaire(questinonaireId));
    } catch (err) {
      toast.error(i18n.t('common:internalError'));
      throw err;
    } finally {
      dispatch(slice.actions.saveQuestionnaireAnswersCompleted());
    }
  }
};
export const finishQuestionnaireAnswers = (): AppThunk => async (dispatch, getState) => {
  const questinonaireId = getState().questionnaire.applicationQuestionnaire?.id;
  const questinonaireAnswers = getState().questionnaire.questionnaireAnswers;

  if (questinonaireId) {
    dispatch(slice.actions.saveQuestionnaireAnswersStarted());

    try {
      await questionnaireApi.applicationQuestionnaireFinish({
        uuid: questinonaireId,
        applicationQuestionnaireAnswerInputRequest: {
          answers: Object.entries(questinonaireAnswers).map(([k, v]) => ({
            id: k,
            answer: v,
          })),
        },
      });

      await dispatch(fetchApplicationQuestionnaire(questinonaireId));
    } catch (err) {
      toast.error(i18n.t('common:internalError'));
      throw err;
    } finally {
      dispatch(slice.actions.saveQuestionnaireAnswersCompleted());
    }
  }
};
export const submitQuestionnaireAnswers = (): AppThunk => async (dispatch, getState) => {
  const questinonaireId = getState().questionnaire.applicationQuestionnaire?.id;
  const questinonaireAnswers = getState().questionnaire.questionnaireAnswers;

  if (questinonaireId) {
    dispatch(slice.actions.saveQuestionnaireAnswersStarted());

    try {
      await questionnaireApi.applicationQuestionnaireResubmit({
        uuid: questinonaireId,
        applicationQuestionnaireAnswerInputRequest: {
          answers: Object.entries(questinonaireAnswers).map(([k, v]) => ({
            id: k,
            answer: v,
          })),
        },
      });

      await dispatch(fetchApplicationQuestionnaire(questinonaireId));
    } catch (err) {
      toast.error(i18n.t('common:internalError'));
      throw err;
    } finally {
      dispatch(slice.actions.saveQuestionnaireAnswersCompleted());
    }
  }
};

export default slice.reducer;
